// ---------------------------------------------------------------------------
// UI primitives
// ---------------------------------------------------------------------------
// Buttons, fields, tables, modals, drawers, tabs, toasts, empty and loading
// states. Deliberately small and unopinionated — the styling lives in
// base.css, and these exist to stop twenty screens each inventing their own
// modal focus trap.
// ---------------------------------------------------------------------------

import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Info, Loader2, X } from 'lucide-react';

// --- Button ---------------------------------------------------------------

export function Button({
  variant = 'default',
  size = 'md',
  icon: Icon,
  loading = false,
  children,
  className = '',
  ...rest
}) {
  const classes = [
    'btn',
    variant === 'primary' && 'btn-primary',
    variant === 'danger' && 'btn-danger',
    variant === 'ghost' && 'btn-ghost',
    size === 'sm' && 'btn-sm',
    size === 'lg' && 'btn-lg',
    !children && 'btn-icon',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} disabled={loading || rest.disabled} {...rest}>
      {loading ? <Loader2 size={14} className="spin" /> : Icon ? <Icon size={14} /> : null}
      {children}
    </button>
  );
}

// --- Fields ---------------------------------------------------------------

/**
 * A labelled form control.
 *
 * The label is wired to the control automatically: an id is generated and
 * pushed onto the child input, and the hint or error is linked with
 * aria-describedby. Doing it here rather than asking every call site to pass
 * `htmlFor` is the difference between a form that a screen reader can navigate
 * and one that reads as a pile of unlabelled boxes — and forty-odd forms will
 * not all remember.
 */
export function Field({ label, hint, error, required, children, htmlFor }) {
  const generatedId = useId();
  const describedById = `${generatedId}-desc`;

  // Only a single element child can be wired up; anything else (a group of
  // radio cards, say) is left alone and labels itself.
  const onlyChild = isValidElement(children) ? children : null;
  const controlId = htmlFor || onlyChild?.props?.id || (onlyChild ? generatedId : undefined);

  const control = onlyChild
    ? cloneElement(onlyChild, {
        id: controlId,
        'aria-describedby':
          [onlyChild.props['aria-describedby'], hint || error ? describedById : null]
            .filter(Boolean)
            .join(' ') || undefined,
        'aria-invalid': error ? true : onlyChild.props['aria-invalid'],
        'aria-required': required ? true : onlyChild.props['aria-required'],
      })
    : children;

  return (
    <div className="field">
      {label && (
        <label className="field-label" htmlFor={controlId}>
          {label}
          {required && <span className="field-required" aria-hidden="true">*</span>}
        </label>
      )}
      {control}
      {error ? (
        <span id={describedById} className="field-error" role="alert">
          <AlertCircle size={12} /> {error}
        </span>
      ) : (
        hint && <span id={describedById} className="field-hint">{hint}</span>
      )}
    </div>
  );
}

export function Input({ error, ...rest }) {
  return <input className={`input${error ? ' input-invalid' : ''}`} {...rest} />;
}

export function Textarea({ error, ...rest }) {
  return <textarea className={`textarea${error ? ' textarea-invalid' : ''}`} {...rest} />;
}

export function Select({ error, options = [], placeholder, children, ...rest }) {
  return (
    <select className={`select${error ? ' select-invalid' : ''}`} {...rest}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) =>
        typeof o === 'string' ? (
          <option key={o} value={o}>{o}</option>
        ) : (
          <option key={o.value} value={o.value}>{o.label}</option>
        ),
      )}
      {children}
    </select>
  );
}

/**
 * A labelled group of mutually exclusive choices, rendered as cards.
 *
 * Used by the impact assessment, where a plain <select> would hide the options
 * that determine someone's priority behind a click. Making all of them visible
 * is the point: the customer should see what they are choosing between.
 */
export function ChoiceGroup({ name, value, onChange, options, columns = 1, describe }) {
  return (
    <div
      role="radiogroup"
      aria-label={name}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 'var(--s2)',
      }}
    >
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            style={{
              textAlign: 'left',
              padding: 'var(--s2) var(--s3)',
              border: `1px solid ${selected ? 'var(--accent)' : 'var(--border-strong)'}`,
              background: selected ? 'var(--accent-subtle)' : 'var(--bg-raised)',
              color: 'var(--fg)',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              transition: 'border-color var(--transition), background var(--transition)',
            }}
          >
            <span style={{ fontWeight: selected ? 600 : 500, fontSize: 'var(--text-base)' }}>
              {option.label}
            </span>
            {describe?.[option.value] && (
              <span className="xs muted">{describe[option.value]}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function Checkbox({ label, checked, onChange, hint, ...rest }) {
  const id = useId();
  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--s2)' }}>
      <input
        id={id}
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(e) => onChange?.(e.target.checked)}
        style={{ marginTop: 2, width: 14, height: 14, accentColor: 'var(--accent)' }}
        {...rest}
      />
      <label htmlFor={id} style={{ cursor: 'pointer' }}>
        <span style={{ fontSize: 'var(--text-base)' }}>{label}</span>
        {hint && <div className="xs muted">{hint}</div>}
      </label>
    </div>
  );
}

// --- Panel ---------------------------------------------------------------

export function Panel({ title, actions, children, flush = false, footer, icon: Icon, className = '' }) {
  return (
    <section className={`panel ${className}`}>
      {(title || actions) && (
        <header className="panel-header">
          <h3 className="panel-title">
            {Icon && <Icon size={14} className="subtle" />}
            {title}
          </h3>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={flush ? 'panel-body-flush' : 'panel-body'}>{children}</div>
      {footer && <footer className="panel-footer">{footer}</footer>}
    </section>
  );
}

// --- Tabs ----------------------------------------------------------------

export function Tabs({ tabs, active, onChange, counts }) {
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        gap: 2,
        borderBottom: '1px solid var(--border)',
        overflowX: 'auto',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === active;
        const count = counts?.[tab.value];
        return (
          <button
            key={tab.value}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 var(--s3)',
              height: 34,
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
              color: isActive ? 'var(--fg)' : 'var(--fg-muted)',
              fontWeight: isActive ? 600 : 500,
              fontSize: 'var(--text-base)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginBottom: -1,
            }}
          >
            {tab.icon && <tab.icon size={13} />}
            {tab.label}
            {count !== undefined && count !== null && (
              <span
                className="mono tabular"
                style={{
                  fontSize: 'var(--text-xs)',
                  padding: '1px 5px',
                  borderRadius: 'var(--radius-sm)',
                  background: isActive ? 'var(--accent-subtle)' : 'var(--bg-sunken)',
                  color: isActive ? 'var(--accent)' : 'var(--fg-muted)',
                  fontWeight: 600,
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// --- Modal ---------------------------------------------------------------

/**
 * Modal with a focus trap and Escape handling.
 *
 * Every destructive or reason-requiring action in this product uses one, so
 * getting the keyboard behaviour right once matters more than it looks.
 */
export function Modal({ open, onClose, title, children, footer, width = 520 }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;

      const focusable = ref.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the first control rather than the dialog, so typing works at once.
    const timer = setTimeout(() => {
      const target = ref.current?.querySelector(
        'input:not([type="hidden"]), textarea, select, button',
      );
      target?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      clearTimeout(timer);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12, 15, 22, 0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: 'var(--s7) var(--s4)',
        zIndex: 'var(--z-modal)',
        overflowY: 'auto',
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{
          background: 'var(--bg-raised)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          width: '100%',
          maxWidth: width,
          maxHeight: 'calc(100vh - var(--s9))',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header className="panel-header" style={{ flexShrink: 0 }}>
          <h3 className="panel-title">{title}</h3>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close" />
        </header>
        <div style={{ padding: 'var(--s4)', overflowY: 'auto', flex: 1 }}>{children}</div>
        {footer && (
          <footer
            className="panel-footer"
            style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 'var(--s2)' }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Slide-over, for the log viewer and the tablet context panel. */
export function Drawer({ open, onClose, title, children, width = 560 }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12, 15, 22, 0.45)',
        zIndex: 'var(--z-drawer)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--bg-raised)',
          borderLeft: '1px solid var(--border)',
          width: '100%',
          maxWidth: width,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <header className="panel-header" style={{ flexShrink: 0 }}>
          <h3 className="panel-title">{title}</h3>
          <Button variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close" />
        </header>
        <div style={{ overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// --- Toasts --------------------------------------------------------------

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone, message, { detail, duration = tone === 'error' ? 8000 : 4000 } = {}) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((current) => [...current, { id, tone, message, detail }]);
      if (duration) setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      success: (m, o) => push('success', m, o),
      error: (m, o) => push('error', m, o),
      info: (m, o) => push('info', m, o),
      /**
       * Report a caught API error. Surfaces the incident id, which is the one
       * thing that makes a failure traceable when a user reports it later.
       */
      apiError: (err, fallback = 'Something went wrong.') =>
        push('error', err?.message || fallback, {
          detail: err?.incidentId ? `Incident ${err.incidentId}` : undefined,
        }),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--s4)',
            right: 'var(--s4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--s2)',
            zIndex: 'var(--z-toast)',
            maxWidth: 380,
          }}
        >
          {toasts.map((t) => {
            const Icon = t.tone === 'success' ? CheckCircle2 : t.tone === 'error' ? AlertCircle : Info;
            const color =
              t.tone === 'success' ? 'var(--success)' : t.tone === 'error' ? 'var(--danger)' : 'var(--info)';
            return (
              <div
                key={t.id}
                role="status"
                style={{
                  display: 'flex',
                  gap: 'var(--s2)',
                  alignItems: 'flex-start',
                  padding: 'var(--s3)',
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 'var(--radius)',
                  boxShadow: 'var(--shadow)',
                }}
              >
                <Icon size={15} style={{ color, flexShrink: 0, marginTop: 1 }} />
                <div className="grow">
                  <div style={{ fontSize: 'var(--text-base)' }}>{t.message}</div>
                  {t.detail && <div className="xs mono subtle">{t.detail}</div>}
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-subtle)', padding: 0 }}
                >
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside a ToastProvider.');
  return ctx;
}

// --- States --------------------------------------------------------------

export function EmptyState({ icon: Icon, title, children, action }) {
  return (
    <div className="empty">
      {Icon && <Icon size={26} className="empty-icon" />}
      {title && <div className="empty-title">{title}</div>}
      {children && <div className="small" style={{ maxWidth: 420 }}>{children}</div>}
      {action}
    </div>
  );
}

/** Table skeleton — matches real row height so the layout does not jump. */
export function TableSkeleton({ rows = 8, columns = 6 }) {
  return (
    <div className="table-wrap">
      <table className="data">
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {Array.from({ length: columns }).map((__, c) => (
                <td key={c}>
                  <div className="skel" style={{ height: 12, width: c === 1 ? '80%' : '55%' }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PanelSkeleton({ lines = 4, height = 12 }) {
  return (
    <div className="col" style={{ gap: 'var(--s2)' }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skel"
          style={{ height, width: `${100 - i * 12}%` }}
        />
      ))}
    </div>
  );
}

export function Spinner({ size = 15 }) {
  return <Loader2 size={size} className="spin" />;
}

export function ErrorState({ error, onRetry }) {
  return (
    <EmptyState icon={AlertCircle} title="That did not load">
      <div>{error?.message || 'An unexpected error occurred.'}</div>
      {error?.incidentId && (
        <div className="xs mono subtle" style={{ marginTop: 4 }}>
          Incident {error.incidentId}
        </div>
      )}
      {onRetry && (
        <div style={{ marginTop: 'var(--s3)' }}>
          <Button onClick={onRetry}>Try again</Button>
        </div>
      )}
    </EmptyState>
  );
}

// --- Pagination ----------------------------------------------------------

export function Pagination({ page, pages, total, limit, onChange }) {
  if (!pages || pages <= 1) {
    return total ? <span className="small muted">{total} result{total === 1 ? '' : 's'}</span> : null;
  }
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="row-between" style={{ padding: 'var(--s2) var(--s3)' }}>
      <span className="small muted tabular">
        {from}–{to} of {total}
      </span>
      <div className="row" style={{ gap: 'var(--s1)' }}>
        <Button size="sm" icon={ChevronLeft} disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="Previous page" />
        <span className="small mono tabular" style={{ padding: '0 var(--s2)' }}>
          {page} / {pages}
        </span>
        <Button size="sm" icon={ChevronRight} disabled={page >= pages} onClick={() => onChange(page + 1)} aria-label="Next page" />
      </div>
    </div>
  );
}

// --- Metric tile ---------------------------------------------------------

/** A single number with its label. Deliberately plain — a KPI card that
    shouts makes every number look equally important. */
export function Metric({ label, value, tone, hint, icon: Icon, onClick }) {
  const color =
    tone === 'critical' ? 'var(--critical)'
    : tone === 'warning' ? 'var(--warning)'
    : tone === 'success' ? 'var(--success)'
    : 'var(--fg)';

  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={onClick}
      className="panel"
      style={{
        padding: 'var(--s3) var(--s4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        textAlign: 'left',
        cursor: onClick ? 'pointer' : 'default',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <div className="row" style={{ gap: 6 }}>
        {Icon && <Icon size={12} className="subtle" />}
        <span className="section-label">{label}</span>
      </div>
      <span className="tabular" style={{ fontSize: 'var(--text-2xl)', fontWeight: 600, color, lineHeight: 1.15 }}>
        {value ?? '—'}
      </span>
      {hint && <span className="xs muted">{hint}</span>}
    </Tag>
  );
}

export { createPortal };
